import { BaseService } from "../../core/baseService";
import { ApiResult } from "../../core/types";
import {
  UserManagementServiceInterface,
  GetStaffUserByIdParams,
  AssignRoleParams,
  RemoveRoleParams,
  RemoveStaffUserParams,
  GetRolesParams,
  CreateRoleParams,
  UpdateRoleParams,
  DeleteRoleParams,
  InviteManagerParams,
} from "../types/userManagement.types";
import {
  StaffUserWithRoles,
  RoleDetail,
  PagePermission,
  PaginatedResponse,
  StaffInvitationSummary,
} from "../../../models";

export class UserManagementService
  extends BaseService
  implements UserManagementServiceInterface
{
  // Staff Users
  async getStaffUserById(
    params: GetStaffUserByIdParams,
  ): Promise<ApiResult<StaffUserWithRoles>> {
    const { staffUserId } = params;
    return this.handleRequest<StaffUserWithRoles>(
      this.client.get(`/api/usermanagement/staffusers/${staffUserId}`),
    );
  }

  async getStaffUsersWithRoles(): Promise<ApiResult<StaffUserWithRoles[]>> {
    return this.handleRequest<StaffUserWithRoles[]>(
      this.client.get("/api/admin/user-management/staff-users"),
    );
  }

  async getPendingInvitations(): Promise<ApiResult<StaffInvitationSummary[]>> {
    return this.handleRequest<StaffInvitationSummary[]>(
      this.client.get("/api/admin/user-management/invitations/pending"),
    );
  }

  async revokeInvitation(invitationId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/user-management/invitations/${invitationId}`),
    );
  }

  async assignRoleToStaffUser(
    params: AssignRoleParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(
        "/api/admin/user-management/staff-users/assign-role",
        params,
      ),
    );
  }

  async removeRoleFromStaffUser(
    params: RemoveRoleParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(
        "/api/admin/user-management/staff-users/remove-role",
        params,
      ),
    );
  }

  async removeStaffUser(
    params: RemoveStaffUserParams,
  ): Promise<ApiResult<void>> {
    const { staffUserId } = params;
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/user-management/staff-users/${staffUserId}`),
    );
  }

  async deactivateStaffUser(
    staffUserId: string,
    reason: string,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(`/api/admin/user-management/staff-users/${staffUserId}/deactivate`, { reason }),
    );
  }

  async activateStaffUser(staffUserId: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post(`/api/admin/user-management/staff-users/${staffUserId}/activate`),
    );
  }

  // Roles
  async getAllRoles(
    params: GetRolesParams,
  ): Promise<ApiResult<PaginatedResponse<RoleDetail>>> {
    const { page, pageSize } = params;

    const queryParams = {
      pageNumber: page, // Note: API uses pageNumber, not page
      pageSize,
    };

    return this.handleRequest<PaginatedResponse<RoleDetail>>(
      this.client.get("/api/admin/user-management/roles", {
        params: queryParams,
      }),
    );
  }

  async getAvailablePermissions(): Promise<ApiResult<PagePermission[]>> {
    return this.handleRequest<PagePermission[]>(
      this.client.get("/api/admin/user-management/roles/available-permissions"),
    );
  }

  async createRole(params: CreateRoleParams): Promise<ApiResult<RoleDetail>> {
    return this.handleRequest<RoleDetail>(
      this.client.post("/api/admin/user-management/roles", params),
    );
  }

  async updateRole(params: UpdateRoleParams): Promise<ApiResult<RoleDetail>> {
    const { roleId, data } = params;
    return this.handleRequest<RoleDetail>(
      this.client.put(`/api/admin/user-management/roles/${roleId}`, data),
    );
  }

  async deleteRole(params: DeleteRoleParams): Promise<ApiResult<void>> {
    const { roleId } = params;
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/user-management/roles/${roleId}`),
    );
  }

  async inviteManagerToHotel(
    params: InviteManagerParams,
  ): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.post("/api/admin/user-management/invitations", params),
    );
  }
}
